import { lazy, Suspense, ComponentType } from "react";
import { Route, Routes } from "react-router-dom";

import { Spin } from "antd";

import { getUser } from "../services/auth";

// Lazy-loaded page components — each page is split into its own JS chunk
const Dashboard = lazy(() => import("../pages/dashboard/view/index"));
const UserManagement = lazy(() => import("../pages/user/view/index"));
const UserCreate = lazy(() => import("../pages/user/view/CreateUser"));
const UserUpdate = lazy(() => import("../pages/user/view/UpdateUser"));
const Files = lazy(() => import("../pages/files/view/index"));
const FileCreate = lazy(() => import("../pages/files/view/CreateFile"));
const FilesDetails = lazy(() => import("../pages/files/view/FileInfo"));
const FileUpdate = lazy(() => import("../pages/files/view/UpdateFile"));
const FileStatus = lazy(() => import("../pages/files/view/StatusFile"));
const Leave = lazy(() => import("../pages/leave/view/index"));
const LeaveCreate = lazy(() => import("../pages/leave/view/CreateLeave"));
const LeaveUpdate = lazy(() => import("../pages/leave/view/UpdateLeave"));
const Claim = lazy(() => import("../pages/claim/view/index"));
const CreateClaim = lazy(() => import("../pages/claim/view/CreateClaim"));
const UpdateClaim = lazy(() => import("../pages/claim/view/UpdateClaim"));
const Payslip = lazy(() => import("../pages/payslip/view/index"));
const PayslipCreate = lazy(() => import("../pages/payslip/view/CreatePayslip"));
const PayslipUpdate = lazy(() => import("../pages/payslip/view/UpdatePayslip"));
const NotFoundPage = lazy(() => import("../pages/notFoundPage/notFoundPage"));

type RouteConfig = { path: string; Component: ComponentType };

// Route configurations per role — store component types, not JSX instances,
// so no React elements are created at module-load time.
const adminRoutes: RouteConfig[] = [
  { path: "/", Component: Dashboard },
  { path: "/dashboard", Component: Dashboard },
  { path: "/users", Component: UserManagement },
  { path: "/users/new", Component: UserCreate },
  { path: "/users/:id", Component: UserUpdate },
  { path: "/files", Component: Files },
  { path: "/files/new", Component: FileCreate },
  { path: "/files/:id", Component: FilesDetails },
  { path: "/files/:id/edit", Component: FileUpdate },
  { path: "/files/:id/status", Component: FileStatus },
  { path: "/leaves", Component: Leave },
  { path: "/leaves/apply", Component: LeaveCreate },
  { path: "/leaves/:id/edit", Component: LeaveUpdate },
  { path: "/claims", Component: Claim },
  { path: "/claims/new", Component: CreateClaim },
  { path: "/claims/:id/edit", Component: UpdateClaim },
  { path: "/payslips", Component: Payslip },
  { path: "/payslips/new", Component: PayslipCreate },
  { path: "/payslips/:id/edit", Component: PayslipUpdate },
];

const internalRoutes: RouteConfig[] = [
  { path: "/", Component: Dashboard },
  { path: "/dashboard", Component: Dashboard },
  { path: "/users/:id", Component: UserUpdate },
  { path: "/files", Component: Files },
  { path: "/files/new", Component: FileCreate },
  { path: "/files/:id", Component: FilesDetails },
  { path: "/files/:id/edit", Component: FileUpdate },
  { path: "/files/:id/status", Component: FileStatus },
  { path: "/leaves", Component: Leave },
  { path: "/leaves/apply", Component: LeaveCreate },
  { path: "/leaves/:id/edit", Component: LeaveUpdate },
  { path: "/claims", Component: Claim },
  { path: "/claims/new", Component: CreateClaim },
  { path: "/claims/:id/edit", Component: UpdateClaim },
  { path: "/payslips", Component: Payslip },
  { path: "/payslips/new", Component: PayslipCreate },
  { path: "/payslips/:id/edit", Component: PayslipUpdate },
];

const externalRoutes: RouteConfig[] = [
  { path: "/", Component: Dashboard },
  { path: "/dashboard", Component: Dashboard },
  { path: "/users/:id", Component: UserUpdate },
  { path: "/files", Component: Files },
  { path: "/files/:id", Component: FilesDetails },
  { path: "/files/:id/status", Component: FileStatus },
];

export default function RouteList() {
  const user = getUser();

  // Guard: cookie may expire mid-session; PrivateRoute will redirect on next render
  if (!user) return null;

  const getRoutesByRole = (role: string) => {
    switch (role) {
      case "admin":
        return adminRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} Component={Component} />
        ));
      case "branchAdmin":
      case "lawyer":
      case "staff":
        return internalRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} Component={Component} />
        ));
      case "client":
      case "agent":
        return externalRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} Component={Component} />
        ));
      default:
        return <Route path="*" Component={NotFoundPage} />;
    }
  };

  return (
    <Suspense fallback={<Spin size="large" fullscreen />}>
      <Routes>{getRoutesByRole(user.role)}</Routes>
    </Suspense>
  );
}
